import { BookingData } from "../../../types/bookings";
import {
  DeceasedPayload,
  EventListPayload,
  ServicesListPayload,
} from "../types/add-booking";
import { appendContactsPayload } from "./booking-contacts";

export const buildBookingFormData = (dataToSubmit: BookingData): FormData => {
  const formData = new FormData();

  const appendToFormData = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      if (typeof value === "boolean" || typeof value === "number") {
        formData.append(key, value.toString());
      } else if (typeof value === "string") {
        formData.append(key, value);
      } else if (value instanceof Blob) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    }
  };

  const {
    booking_type,

    total,
    email,
    client_salutation,
    phone,
    address_one,
    address_two,
    suburb,
    city,
    customer_address,
    source_of_booking,
    company_name,
    branch_name,
    branch_rep,
    relationship_to_deceased,
    church_name,
    religion,
    contact_id,
    company_id,
    funeral_home_id,
    denomination,
    service_notes,
    special_request,
    title,
  } = dataToSubmit;

  appendToFormData("booking_type", booking_type?.toUpperCase());
  appendToFormData("conference_type", dataToSubmit.conference_type);

  appendToFormData("status", "enquiry");
  appendToFormData("total", total?.toFixed(2));
  appendToFormData("payment_method", "pending");
  appendToFormData("payment_status", "pending");
  appendToFormData("account_status", "active");
  appendToFormData("title", title);
  const effectiveContactId = contact_id ?? company_id;
  appendToFormData("contact_id", effectiveContactId);
  appendContactsPayload(formData, dataToSubmit.contacts, effectiveContactId);
  appendToFormData("company_id", company_id);
  appendToFormData("funeral_home_id", funeral_home_id);
  appendToFormData("email", email);
  appendToFormData("salutation", client_salutation);
  appendToFormData("phone", dataToSubmit.phone_numbers || phone);
  appendToFormData("address_one", address_one);
  appendToFormData("address_two", address_two);
  appendToFormData("suburb", suburb);
  appendToFormData("city", city);
  appendToFormData("customer_address", customer_address);
  appendToFormData(
    "source_of_booking",
    dataToSubmit.source || source_of_booking,
  );
  appendToFormData("referral_partner_id", dataToSubmit.referral_partner_id);
  appendToFormData("company_name", company_name);
  appendToFormData("branch_name", branch_name);
  appendToFormData("branch_rep", branch_rep);
  appendToFormData("relationship_to_deceased", relationship_to_deceased);
  appendToFormData("church_name", church_name);
  appendToFormData("religion", religion);
  appendToFormData("denomination", denomination);
  appendToFormData("service_notes", service_notes);
  appendToFormData("special_request", special_request);

  // Additional Services
  const additionalServiceCategories = [
    "AV Equipment",
    "Audio Visual Equipment",
    "Design",
    "Other",
    "Screens",
    "Video & Audio",
    "Visual Presentation & Audio",
  ];

  const uniqueServiceIds = additionalServiceCategories
    .flatMap((cat) =>
      Array.isArray(dataToSubmit[cat]) ? dataToSubmit[cat] : [],
    )
    .filter(Boolean)
    .filter((id, idx, arr) => arr.indexOf(id) === idx);

  // Deceased List
  const deceasedItems: DeceasedPayload[] = dataToSubmit.deceased_list?.length
    ? dataToSubmit.deceased_list
    : dataToSubmit.deceased_name && dataToSubmit.deceased_surname
      ? [
          {
            deceased_name: dataToSubmit.deceased_name,
            deceased_surname: dataToSubmit.deceased_surname,
            second_name: dataToSubmit.second_name,
            date_of_birth: dataToSubmit.deceased_dob,
            date_of_death: dataToSubmit.deceased_dod,
            gender: dataToSubmit.gender,
            deceased_salutation: dataToSubmit.deceased_salutation,
            nickname: dataToSubmit.deceased_nickname,
            photo_url: dataToSubmit.deceased_photo_url,
            deceasedImageFile: dataToSubmit.deceasedImageFile,
            relationship_to_deceased: relationship_to_deceased,
            religion: religion,
            denomination: denomination,
          },
        ]
      : [];

  deceasedItems.forEach((d, index) => {
    const prefix = `deceased_list[${index}]`;
    appendToFormData(`${prefix}[first_name]`, d.deceased_name);
    appendToFormData(`${prefix}[surname]`, d.deceased_surname);
    appendToFormData(`${prefix}[second_name]`, d.second_name);
    appendToFormData(`${prefix}[date_of_birth]`, d.date_of_birth);
    appendToFormData(`${prefix}[date_of_death]`, d.date_of_death);
    appendToFormData(`${prefix}[gender]`, d.gender);
    appendToFormData(`${prefix}[salutation]`, d.deceased_salutation);
    appendToFormData(`${prefix}[nickname]`, d.nickname);
    appendToFormData(
      `${prefix}[relationship_to_deceased]`,
      d.relationship_to_deceased,
    );
    appendToFormData(`${prefix}[religion]`, d.religion);
    appendToFormData(`${prefix}[denomination]`, d.denomination);
    appendToFormData(`${prefix}[id]`, d.id);

    if (d.deceasedImageFile) {
      formData.append(
        `${prefix}[person_image]`,
        d.deceasedImageFile,
        d.deceasedImageFile.name,
      );
    }
  });

  // Package Item
  if (dataToSubmit.package) {
    const { name, contents, id, price } = dataToSubmit.package;
    appendToFormData("package_item[name]", name);
    appendToFormData(
      "package_item[description]",
      contents?.[0] || "Funeral Package",
    );
    appendToFormData("package_item[package]", id);
    appendToFormData("package_item[price]", price?.toFixed(2));
    uniqueServiceIds.forEach((serviceId) => {
      appendToFormData("package_item[services][]", serviceId);
    });
  }

  // Services List
  if (dataToSubmit.services_list?.length) {
    dataToSubmit.services_list.forEach(
      (service: ServicesListPayload, index: number) => {
        const prefix = `services_list[${index}]`;
        Object.entries(service).forEach(([key, value]) => {
          if (key === "start_time" || key === "end_time") {
            appendToFormData(`${prefix}[${key}]`, value ? `${value}:00` : null);
          } else {
            appendToFormData(`${prefix}[${key}]`, value);
          }
        });
      },
    );
  } else if (dataToSubmit.venue_ids?.length) {
    dataToSubmit.venue_ids.forEach((venueId, index) => {
      const prefix = `services_list[${index}]`;
      appendToFormData(`${prefix}[mc_name]`, dataToSubmit.mc_name);
      appendToFormData(`${prefix}[eulogist_name]`, dataToSubmit.eulogist_name);
      appendToFormData(
        `${prefix}[date_of_service]`,
        dataToSubmit.date_of_service,
      );
      appendToFormData(
        `${prefix}[start_time]`,
        dataToSubmit.start_time ? `${dataToSubmit.start_time}:00` : null,
      );
      appendToFormData(
        `${prefix}[end_time]`,
        dataToSubmit.end_time ? `${dataToSubmit.end_time}:00` : null,
      );
      appendToFormData(
        `${prefix}[service_city]`,
        dataToSubmit.service_city || city,
      );
      appendToFormData(
        `${prefix}[venue_id]`,
        dataToSubmit.venue_room_id ?? parseInt(venueId),
      );
      appendToFormData(`${prefix}[church_name]`, church_name);
      appendToFormData(
        `${prefix}[leader_preacher_name]`,
        dataToSubmit.leader_preacher_name,
      );
      appendToFormData(
        `${prefix}[leader_preacher_surname]`,
        dataToSubmit.leader_preacher_surname,
      );
      appendToFormData(`${prefix}[preacher_name]`, dataToSubmit.preacher_name);
      appendToFormData(
        `${prefix}[preacher_surname]`,
        dataToSubmit.preacher_surname,
      );
      appendToFormData(
        `${prefix}[service_address]`,
        dataToSubmit.service_address,
      );
      appendToFormData(
        `${prefix}[service_location]`,
        dataToSubmit.service_location,
      );
      appendToFormData(
        `${prefix}[type_of_service]`,
        dataToSubmit.type_of_service,
      );
      appendToFormData(`${prefix}[service_notes]`, dataToSubmit.service_notes);
      appendToFormData(
        `${prefix}[special_request]`,
        dataToSubmit.special_request,
      );
    });
  }

  // Event List
  if (dataToSubmit.events_list?.length) {
    dataToSubmit.events_list.forEach(
      (event: EventListPayload, index: number) => {
        const prefix = `events_list[${index}]`;
        appendToFormData(`${prefix}[title]`, event.title);
        appendToFormData(`${prefix}[hosts]`, event.hosts);
        appendToFormData(`${prefix}[host_surname]`, event.host_surname);
        appendToFormData(`${prefix}[guests]`, event.guests);
        appendToFormData(`${prefix}[guest_surname]`, event.guest_surname);
        appendToFormData(`${prefix}[venue_id]`, event.venue_id);
        appendToFormData(
          `${prefix}[start_date_of_event]`,
          dataToSubmit.start_date_of_event,
        );
        appendToFormData(
          `${prefix}[end_date_of_event]`,
          dataToSubmit.end_date_of_event,
        );
        appendToFormData(`${prefix}[start_time]`, dataToSubmit.start_time);
        appendToFormData(`${prefix}[end_time]`, dataToSubmit.end_time);
        appendToFormData(
          `${prefix}[expected_audience_size]`,
          dataToSubmit.expected_audience_size,
        );
        appendToFormData(
          `${prefix}[record_stream]`,
          dataToSubmit.record_stream,
        );
        appendToFormData(
          `${prefix}[enable_livestreaming]`,
          dataToSubmit.enable_livestreaming,
        );
        appendToFormData(
          `${prefix}[streaming_platform]`,
          dataToSubmit.streaming_platform,
        );
        appendToFormData(
          `${prefix}[require_custom_branding]`,
          dataToSubmit.require_custom_branding,
        );
        appendToFormData(
          `${prefix}[retention_duration]`,
          dataToSubmit.retention_duration,
        );
        appendToFormData(
          `${prefix}[branding_requirements]`,
          dataToSubmit.branding_requirements,
        );
        appendToFormData(
          `${prefix}[session_to_stream]`,
          dataToSubmit.session_to_stream,
        );
        appendToFormData(
          `${prefix}[specific_sessions]`,
          dataToSubmit.specific_sessions,
        );
        appendToFormData(`${prefix}[access_type]`, dataToSubmit.access_type);
        appendToFormData(
          `${prefix}[provide_on_demand_access]`,
          dataToSubmit.provide_on_demand_access,
        );
        appendToFormData(
          `${prefix}[service_notes]`,
          dataToSubmit.service_notes,
        );
        appendToFormData(
          `${prefix}[special_request]`,
          dataToSubmit.special_request,
        );
        appendToFormData(
          `${prefix}[conference_type]`,
          dataToSubmit.conference_type,
        );
      },
    );
  } else if (dataToSubmit.title || dataToSubmit.hosts || dataToSubmit.guests) {
    const prefix = `events_list[0]`;
    appendToFormData(`${prefix}[title]`, dataToSubmit.title || "Memorial");
    appendToFormData(`${prefix}[hosts]`, dataToSubmit.hosts);
    appendToFormData(`${prefix}[host_surname]`, dataToSubmit.host_surname);
    appendToFormData(`${prefix}[guests]`, dataToSubmit.guests);
    appendToFormData(`${prefix}[guest_surname]`, dataToSubmit.guest_surname);
    const venueId = dataToSubmit.venue_ids?.[0];
    if (venueId && parseInt(venueId) > 0) {
      appendToFormData(
        `${prefix}[venue_id]`,
        dataToSubmit.venue_room_id ?? parseInt(venueId),
      );
    }
    appendToFormData(`${prefix}[start_time]`, dataToSubmit.start_time);
    appendToFormData(`${prefix}[end_time]`, dataToSubmit.end_time);
    appendToFormData(
      `${prefix}[expected_audience_size]`,
      dataToSubmit.expected_audience_size,
    );
    appendToFormData(`${prefix}[record_stream]`, dataToSubmit.record_stream);
    appendToFormData(
      `${prefix}[streaming_platform]`,
      dataToSubmit.streaming_platform,
    );
    appendToFormData(
      `${prefix}[enable_livestreaming]`,
      dataToSubmit.enable_livestreaming,
    );
    appendToFormData(
      `${prefix}[start_date_of_event]`,
      dataToSubmit.start_date_of_event,
    );
    appendToFormData(
      `${prefix}[end_date_of_event]`,
      dataToSubmit.end_date_of_event,
    );
    appendToFormData(
      `${prefix}[require_custom_branding]`,
      dataToSubmit.require_custom_branding,
    );
    appendToFormData(
      `${prefix}[retention_duration]`,
      dataToSubmit.retention_duration,
    );
    appendToFormData(
      `${prefix}[branding_requirements]`,
      dataToSubmit.branding_requirements,
    );
    appendToFormData(
      `${prefix}[session_to_stream]`,
      dataToSubmit.session_to_stream,
    );
    appendToFormData(
      `${prefix}[provide_on_demand_access]`,
      dataToSubmit.provide_on_demand_access,
    );
    appendToFormData(
      `${prefix}[specific_sessions]`,
      dataToSubmit.specific_sessions,
    );
    appendToFormData(`${prefix}[access_type]`, dataToSubmit.access_type);
    appendToFormData(`${prefix}[service_notes]`, dataToSubmit.service_notes);
    appendToFormData(
      `${prefix}[special_request]`,
      dataToSubmit.special_request,
    );
    appendToFormData(
      `${prefix}[conference_type]`,
      dataToSubmit.conference_type,
    );
  }

  return formData;
};
